/* @noflow */
/* eslint-disable no-unused-vars */

import _ from 'lodash';
import { getNextFieldsByStep, getStepByField } from '../utils/stepHelpers';
import { Steps } from '../utils/constants';
import HashId from '../utils/hashid';

import { constants, undoableActions } from '../actions/design';
import type { StateType, DesignResponseType, RecordDataType, FieldDataType } from '../actions/design';

import { nextStep, prevStep } from '../selectors/design';

import designRoutines from '../routines/design';

const hashId = new HashId();

export const initialState: StateType = {
  loading: true,
  initialValues: {},
  name: '',
  id: null,
  currentStep: Steps.STEP_ONE,
  dirtyFields: {},
  changedFields: {},
  values: {},
  evaluating: {},
  errors: {},
  chartData: false,
  schematic: {
    loading: false,
    hasData: false,
    data: '',
  },
  fetConfig: ['', '', ''],
  stepStatus: {},
  openingDesign: false,
  savingDesign: false,
};

type ActionType = {
  data: any,
  payload: any,
  type: any,
};

function design(state: StateType = initialState, action: ActionType): StateType {
  switch (action.type) {
    case constants.CHANGE_VALUE: {
      const field = _.get(action, ['payload', 'field']);
      const value = _.get(action, ['payload', 'value']);
      const revertValue: boolean = _.get(action, ['payload', 'revert']);

      const newState = {
        ...state,
        changedFields: {
          ...state.changedFields,
          [field]: !revertValue,
        },
        dirtyFields: {
          ...state.dirtyFields,
          [field]: state.values[field].currentValue,
        },
        errors: _.omit(state.errors, [field]),
      };

      // Insert the correct type info for the selected package.
      // TODO: Clean this up
      let values = state.values;
      if (field === 'SpmPackage') {
        const parts = _.get(state.values, ['_TypeInfo', revertValue ? state.initialValues[field].currentValue : value]);

        values = {
          ...values,
          PartNumber: {
            currentValue: _.get(parts, ['0', 'partNumber']),
            defaultValue: _.get(parts, ['0', 'partNumber']),
          },
          PartNumberInfoList: {
            currentValue: parts,
            defaultValue: null,
          },
        };
      }

      // Hack to reset the report generators (saga listens for CHANGE_VALUE)
      values = {
        ...values,
        Figure1: {
          currentValue: null,
        },
      };

      // To revert a field eval without that field
      if (revertValue) {
        return {
          ...newState,
          values: {
            ...values,
            [field]: {
              ...state.initialValues[field],
            },
          },
        };
      }

      return {
        ...newState,
        values: {
          ...values,
          [field]: {
            ...values[field],
            currentValue: value,
          },
        },
      };
    }

    case constants.EVALUATE: {
      const field = _.get(action, ['payload', 'field']);
      return {
        ...state,
        evaluating: {
          ...state.evaluating,
          [field]: true,
        },
        dirtyFields: {
          ...state.dirtyFields,
          [field]: state.values[field].currentValue,
        },
      };
    }

    case constants.PENDING: {
      return {
        ...state,
        loading: true,
      };
    }

    case constants.JUMP_STEP: {
      return {
        ...state,
        currentStep: action.payload,
      };
    }

    case constants.AUTO_COMPLETE: {
      return {
        ...state,
        currentStep: Steps.STEP_ANALYSIS,
      };
    }

    case constants.STEP_FORWARD: {
      const next = nextStep(state.currentStep);
      return {
        ...state,
        currentStep: next,
      };
    }

    case constants.STEP_BACKWARD: {
      const prev = prevStep(state.currentStep);
      return {
        ...state,
        currentStep: prev,
      };
    }

    case constants.RENAME_DESIGN: {
      return {
        ...state,
        name: action.data,
      };
    }

    case constants.STEP_RESET: {
      // We need to reset fields values for all stages for/after this one.
      // const active = state.currentStep;
      // const fields = getNextFieldsByStep(active);
      return {
        // ...state.initialValues,
        ...state,
        loading: false,
        values: {
          ...state.initialValues,
        },
        // changedFields: _.omit(state.changedFields, fields, {}),
        // dirtyFields: _.omit(state.dirtyFields, fields, {}),
        // errors: _.omit(state.errors, fields, {}),
        errors: {},
        dirtyFields: {},
        changedFields: {},
      };
    }

    case constants.SAVE_DESIGN: {
      return {
        ...state,
        name: action.payload,
        savingDesign: true,
      };
    }

    case designRoutines.fetchRecord.TRIGGER: {
      return {
        ...state,
        loading: true,
      };
    }

    // case constants.RECEIVE_DESIGN: {
    case designRoutines.fetchRecord.SUCCESS: {
      // const data: FieldDataType = action.payload.data;
      // const record: RecordDataType = action.payload.message.record;

      const response = action.payload.response;
      const {
        actionType, activityDttm, activityId,
        emailId, recordName, userId, userIpAddress,
        type, ...rest
      } = response;

      const formatField = (str: string) => {
        const fields = {
          ambientTmpt: 'Tamb',
          csHtsnkThrmlRest: 'RthCS',
          dcBusVolt: 'Vbus',
          maxHeatSinkTmpt: 'TSinkMax',
          maxJunctnTmpt: 'TjMax1',
          modulatnIdx: 'MI',
          motorRmsPhsCurr: 'IPhase',
          outputFreq: 'OutputFreq',
          overloadPctg: 'OverLoadPercent',
          partSelected: 'PartNumber',
          pkg: 'SpmPackage',
          pwmMethod: 'PWM',
          pwrFctr: 'PF',
          snkAmbThrmlRest: 'RthSA1',
          snkAmbThrmlRestCalcMtd: 'ManualRSink',
          switchingFreq: 'SwitchingFreqKhz',
        };

        return _.get(fields, str, 'UNKNOWN');
      };

      const data = _.reduce(
        rest,
        (obj: any, val: string, key: string) => {
          const o = obj;
          o[formatField(key)] = {
            currentValue: val,
            // defaultValue: val,
          };
          return o;
        }, {},
      );

      // update the package list since this is 'calculated'
      // eslint-disable-next-line
      state.values.PartNumberInfoList.currentValue = state.values._TypeInfo[rest.pkg];

      const changedFields: FieldDataType = _.pickBy(
        data,
        (field: Object, key: string) =>
          // eslint-disable-next-line eqeqeq
          _.isString(getStepByField(key)) && field.currentValue != state.initialValues[key].currentValue,
      );

      return {
        ...state,
        id: activityId,
        values: _.merge({}, state.values, data),
        changedFields,
        name: recordName,
        loading: false,
        currentStep: Steps.STEP_ONE,
      };
    }

    case constants.NEW_DESIGN: {
      return {
        ...initialState,
        id: null,
      };
    }

    case constants.UPDATE: {
      // const { data, content, messageFor, message, errorSource } = action.payload;
      const payload: DesignResponseType = action.payload;
      const emptyState = _.isEmpty(state.initialValues);
      const data = _.omitBy(
        payload.data,
        (val: any, key: string) => (['BOM'].includes(key) || key.includes('Col_')),
      );

      const initialOrCurrent = f => _.get(
        emptyState ? data : state.initialValues,
        [f, 'currentValue'],
      );

      const fixFields = [
        'Therm_opt', 'PartNumber', 'Opmode',
        'Snub_sel', 'IndPNsel',
      ];

      const fixedDefaultValues = fixFields.reduce(
        (obj: {}, f: string) => Object.assign({}, obj, {
          [f]: { defaultValue: initialOrCurrent(f) },
        }), {},
      );

      const values = _.merge(data, fixedDefaultValues);

      const fieldRemap = {
        SuitableInductors: 'IndPNsel',
        Pkgs_FET0: 'Pkgs_FET',
        //   t_delay_hson: 'T_delay_hson',
        //   t_delay_lson: 'T_delay_lson',
        //   etaest_hl: 'Etaest_hl',
        //   etaest_ll: 'Etaest_ll',
        //   DC_CI: 'DF_CI',
      };

      const messageFor = _.get(payload, ['messageFor'], {});
      const content = _.get(payload, ['content'], {});
      const messageType = _.get(payload, ['type']) || _.get(payload, ['message', 'type']);

      const field = fieldRemap[messageFor] || messageFor;
      let updatedValues = values;

      if (field) {
        // field = field.replace(/\u0394/, 'Delta');
        // field = field.replace(/\u03B7/, 'Eta');
      }

      // const type = get(message, 'type') || get(action.payload, 'type', 'NONE');
      const errorSource = _.get(payload, ['errorSource'], {});

      if (messageType === 'ERROR') {
        updatedValues = _.merge(values, {
          [errorSource.key]: {
            currentValue: errorSource.currentValue,
          },
        });
      }

      const errorObj = (messageType === 'SUCCESS')
        ? {}
        : { [field]: [content] };

      // TODO: fix this - hack to reset Figure legends
      const figures = Array(12).fill(0).map((v, i) => `Figure${i + 1}`);
      // eslint-disable-next-line no-param-reassign
      state.values = _.omit(state.values, figures);

      return {
        ...state,
        loading: false,
        initialValues: emptyState ? values : state.initialValues,
        values: _.merge(state.values, updatedValues),
        evaluating: {},
        dirtyFields: {},
        errors: {},
        chartData: initialState.chartData,
        schematic: initialState.schematic,
        savingDesign: false,
        openingDesign: false,
      };
    }

    case constants.VALIDATION_ERROR: {
      const { field, message } = action.payload;

      return {
        ...state,
        errors: {
          ...state.errors,
          [field]: _.uniq([].concat(state.errors[field], message).filter(i => i)),
        },
        evaluating: {},
      };
    }

    case constants.REQUEST_CHART_DATA: {
      return {
        ...state,
        loading: true,
      };
    }
    // case constants.RECEIVE_CHART_DATA: {
    //   return {
    //     ...state,
    //     loading: false,
    //     chartData: true,
    //   };
    // }
    case constants.REQUEST_SCHEMATIC: {
      return {
        ...state,
        schematic: {
          loading: true,
          hasData: false,
          data: '',
        },
      };
    }

    case constants.RECEIVE_SCHEMATIC: {
      const svg = _.get(action.payload, ['response'], '');

      return {
        ...state,
        schematic: {
          loading: false,
          hasData: true,
          data: svg.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg" height="500" width="100%"'),
        },
      };
    }

    case constants.RECEIVE_FET_CONFIG: {
      return {
        ...state,
        fetConfig: action.payload,
      };
    }

    default: return state;
  }
}

export default design;



// WEBPACK FOOTER //
// ./src/reducers/design.js